import React, { useEffect, useRef, useState, useMemo } from 'react';
import { InputAdornment, TextField, IconButton, Box } from '@mui/material';
import debounce from 'lodash/debounce';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch } from 'react-redux';

export const SearchBox = ({ filters, setConfig, debounceDelay = 300, ...params }) => {

    const dispatch = useDispatch();
    const inputRef = useRef(null);
    const [searchValue, setSearchValue] = useState(filters?.keyword || '');
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        if (isExpanded && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isExpanded]);

    const debouncedSearchChange = useMemo(() =>
        debounce((newSearchValue) => {
            if (newSearchValue.length >= 3 || newSearchValue.length === 0) {
                dispatch(setConfig({ filters: { ...filters, keyword: newSearchValue } }));
            }
        }, debounceDelay), [debounceDelay, dispatch, filters, setConfig]);

    const handleChange = (e) => {
        const newSearchValue = e.target.value;
        setSearchValue(newSearchValue);
        debouncedSearchChange(newSearchValue);
    };

    const handleClear = () => {
        setSearchValue('');
        dispatch(setConfig({ ...filters, keyword: '' }));
        debouncedSearchChange('');
    };

    useEffect(() => {
        return () => {
            debouncedSearchChange.cancel();
        };
    }, [debouncedSearchChange]);

    const handleExpandToggle = () => {
        setIsExpanded(!isExpanded);
        if (!isExpanded) setSearchValue('');
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>

            <Box sx={{ display: { xs: isExpanded ? 'none' : 'flex', md: 'none' } }}>
                <IconButton onClick={handleExpandToggle}>
                    <SearchIcon />
                </IconButton>
            </Box>

            <Box sx={{ display: { xs: isExpanded ? 'block' : 'none', md: 'block' }, flex: 1 }}>
                <TextField
                    sx={{
                        '& .MuiInputBase-input': {
                            padding: '8px 14px',
                        },
                        p: 0
                    }}
                    value={searchValue}
                    onChange={handleChange}
                    inputRef={inputRef}
                    placeholder="Search..."
                    {...params}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                {searchValue.length > 0 && (
                                    <IconButton onClick={handleClear}>
                                        <ClearIcon fontSize={"small"} />
                                    </IconButton>
                                )}
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
        </Box>
    );
};
